import AppContext from "../utils/app-context.js";
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Link
} from "@mui/material";
import assets from "../utils/assets";
import Messages from "../constants/app";
import toast from "react-hot-toast";
import { doc, setDoc, getDoc, serverTimestamp, increment, collection } from "@firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "@firebase/storage"
import { emptyStory, sortTakes, getMediaUrl, incrementShotTitle } from "../utils/utility.js";
import { db, storage, auth } from "../utils/firebase"
import { nanoid } from "nanoid";
import hostConfig from "../utils/config";
import { methods, baseVideos } from "../constants/index.js"

/**
 * Compute the credit split between project and user based on the projectThenUser policy.
 * Returns { projectAmount, userAmount, payer } where payer is a map of uid -> credits.
 */
export const computeCreditSplit = async (storyRef, cost, currentUserUid) => {
  if (!storyRef) {
    return { projectAmount: 0, userAmount: cost, payer: { [currentUserUid]: cost } };
  }
  try {
    const projectBalanceRef = doc(storyRef, 'credits', '0');
    const projectSnap = await getDoc(projectBalanceRef);

    if (!projectSnap.exists() || projectSnap.data().policy !== 'projectThenUser') {
      // No project credits doc or no policy → all from user
      return { projectAmount: 0, userAmount: cost, payer: { [currentUserUid]: cost } };
    }

    const projectCredits = projectSnap.data().credits || 0;
    if (projectCredits <= 0) {
      return { projectAmount: 0, userAmount: cost, payer: { [currentUserUid]: cost } };
    }

    // Determine story owner from storyRef path: /users/{uid}/stories/{storyId}
    const pathParts = storyRef.path.split('/');
    const userIndex = pathParts.findIndex((item) => item === 'users') + 1;
    const ownerUid = pathParts[userIndex];

    const projectAmount = Math.min(projectCredits, cost);
    const userAmount = cost - projectAmount;

    const payer = {};
    if (projectAmount > 0) payer[ownerUid] = projectAmount;
    if (userAmount > 0) payer[currentUserUid] = userAmount;

    return { projectAmount, userAmount, payer };
  } catch (error) {
    console.error('Error computing credit split:', error);
    return { projectAmount: 0, userAmount: cost, payer: { [currentUserUid]: cost } };
  }
};

export const uploadBlob = (props) => {
  const { blob, dispatch, path, firebase } = props;
  const storageRef = ref(storage, path);

  dispatch({ type: "START_UPLOAD", payload: { totalBytes: blob.size, totalFiles: 1 } });

  let uploadResolve, uploadReject;
  const uploadPromise = new Promise((resolve, reject) => {
    uploadResolve = resolve;
    uploadReject = reject;
  });

  //const blobRef = storageRef.child(path)

  const uploadNext = (snapshot) => {
    // const percent = snapshot.bytesTransferred / snapshot.totalBytes * 100;
    const bytes = snapshot.bytesTransferred;
    dispatch({ type: "FILE_UPLOAD_UPDATE", payload: { path, bytes } });
  }

  const uploadError = (error) => {
    console.log(`Error uploading blob`, error)
    uploadReject(error)
  }

  const uploadComplete = () => {
    dispatch({ type: "FILE_UPLOAD_SUCCESS", payload: {} });
    uploadResolve('upload completed');
  }

  const blobUploadTask = uploadBytesResumable(storageRef, blob);

  blobUploadTask.on('state_changed', snapshot => {
    switch (snapshot.state) {
      case 'running':
        uploadNext(snapshot)
        break;
    }
  }, (error) => {
    uploadError()
  }, () => {
    uploadComplete()
  });

  return uploadPromise;
}

export const videoUpload = async (props) => {
  const { file, shot_id, state, dispatch, storyImport, currentUser, firebase, is_ref, callback, source = "upload", addUserRef = false } = props;
  const file_MB = file.size / 1000000
  const alert_thresh_MB = 25
  const is_vertical = state.stories.activeStory.is_vertical || false;
  let success = false;
  let portalId = props.portalId;

  const max_MB = (state.app.userIsStoryOwner && state.app.settings.userCanUploadLargeFiles) ? 500 : 500
  if (!storyImport && file_MB > max_MB) {
    alert(`Please choose a shorter video. ${file_MB.toPrecision(3)}MB is over the ${max_MB}MB limit for this project's settings.`)
  } else if (storyImport && file_MB > max_MB) {
    alert(`Please choose a shorter video. ${file_MB.toPrecision(3)}MB is over the ${max_MB}MB limit for import.`)
  } else {
    if (!storyImport && file_MB > alert_thresh_MB) {
      alert(`This file is larger than expected for the recommended 1-6 second video. Upload will resume.`)
    } else if (storyImport && file_MB > alert_thresh_MB) {
      alert(`This file is larger than expected. Upload will resume, but processing may take longer than usual.`)
    }
    if (storyImport) console.log("onFileUploadChange ... " + "insert story" + " " + file_MB.toPrecision(3) + "MB")
    else console.log("onFileUploadChange ... " + shot_id + " " + file_MB.toPrecision(3) + "MB")

    const id = uuidv4();
    const storageDir = "media"
    let oid = "unknown"
    let sid = "unknown"
    let uploader_id = currentUser ? currentUser.uid : "unknown";
    //const path = `${storageDir}/${filename}.mp4`

    if (storyImport) {
      //handleContinueEmpty({dispatch:dispatch, portalId:portalId});
      // story_path_array[story_path_array.length - 1] = portalId;
      oid = currentUser.uid;
      if (state.stories.activeStory?.portal_id !== portalId) {
        handleContinueEmpty({ dispatch: dispatch, portalId: portalId, includePart: false });
      }
      // path = `${storageDir}/us/${oid}/st/${sid}/up/${uploader_id}/imports/${id}.mp4`
    } else if (state.stories.activeStory && state.stories.activeStory.ref) {
      // const story_path = state.stories.activeStory.ref.u_.path 
      // const OID_ind = story_path.segments.findIndex((item) => item == "users") + 1 
      const story_path_array = state.stories.activeStory.ref.path.split("/");
      const OID_ind = story_path_array.findIndex((item) => item == "users") + 1
      oid = story_path_array[OID_ind]

      const SID_ind = story_path_array.findIndex((item) => item == "stories") + 1
      sid = story_path_array[SID_ind]
    }
    const path = (storyImport) ? `${storageDir}/us/${oid}/st/${portalId || sid}/up/${uploader_id}/imports/${id}.mp4` : `${storageDir}/us/${oid}/st/${sid}/up/${uploader_id}/ta/${id}.mp4`

    const uploadPromise = uploadBlob({ blob: file, dispatch: dispatch, path: path, firebase: firebase })
    const result = await uploadPromise;

    if (result === 'upload completed') {
      success = true;
      //event.target.value = null
      if (storyImport) {
        if (callback) {
          callback();
        }
        const payload = {
          video_path: path,
          thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
          id: id,
          type: "storyboard",
          scene_thresh: 0.3, // sensitive:0.2  normal:0.3   strict: 0.4 
          max_shot_dur_ms: 4000,
          include_audio: state.app.settings.videoImportIncludesAudio,
          is_vertical: is_vertical,
          //storyId: state.stories.activeStory.ref.id || null, 
          portalId: portalId || null,
          currentUser: currentUser,
          source: source
        }
        dispatch({ type: "STORY_IMPORT", payload: payload })
        toast.success(Messages.info.autoStoryBoardStarted);
      } else {
        if (shot_id) {
          const payload = {
            video_path: path,
            thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
            shot_id: shot_id,
            is_vertical: is_vertical,
            take_id: id,
            source: source,
            uploader_email: currentUser?.email || null
          }
          dispatch({ type: "ADD_TAKE_TO_SHOT", payload: payload })
          if (!is_ref && callback) {
            callback();
          }
          //setSelected();
          //setTakesCount(takesCount + 1)
          const lastPayload = {
            take_id: id,
            image: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
            stream_path: path,
            shot_id: shot_id
          };
          dispatch({ type: "UPDATE_SHOT_LAST_TAKE", payload: lastPayload })
        }
        if (is_ref) {
          const take_payload = {
            video_path: path,
            thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
            shot_id: "reference",
            id: id,
            is_ref: true,
            is_vertical: is_vertical
          }
          if (callback) {
            if (callback) callback({ take: take_payload, file: file });
          }
          dispatch({ type: "ADD_TAKE_REF", payload: { take: take_payload, addUserRef: addUserRef } });
        }
        toast.success(Messages.info.mediaUploadCompleted);
      }
    }
  }
  return success;
}

export const imageUpload = async (props) => {
  const { file, state, dispatch, shot_id, is_ref, currentUser, callback, firebase, path, source = "upload", addUserRef = false } = props
  const file_MB = file.size / 1000000
  const max_MB = 15
  const storyRef = state.stories.activeStoryRef;
  const is_vertical = state.stories.activeStory.is_vertical || false;
  if (file_MB > max_MB) {
    alert(`Please use a smaller file size.  Check resolution or compression type (JPG, PNG, WEBP). ${file_MB.toPrecision(3)}MB is over the ${max_MB}MB limit for this project's settings.`)
  } else {
    const take_id = uuidv4();
    const storageDir = "media"
    let oid = currentUser ? currentUser.uid : "unknown";
    let sid = is_ref ? "reference" : "unknown";
    if (state?.stories?.activeStory && state?.stories?.activeStory?.ref) {
      const story_path_array = state.stories.activeStory.ref.path.split("/");
      const OID_ind = story_path_array.findIndex((item) => item == "users") + 1
      oid = story_path_array[OID_ind]

      const SID_ind = story_path_array.findIndex((item) => item == "stories") + 1
      sid = story_path_array[SID_ind]
    }

    if (['image/jpeg', 'image/webp', 'image/png'].includes(file.type)) {
      var ext = file.type.substr(file.type.lastIndexOf('/') + 1)
    }
    else {
      alert("Unsupported file type.  Images should be jpeg, webp or png, not '" + file.type + "' -- " + file.name)
      // var re = /(?:\.([^.]+))?$/;
      // var ext = re.exec(file.name)[1]
    }

    const uploader_id = currentUser ? currentUser.uid : "unknown"
    const image_path = (path) ? `${storageDir}/us/${oid}/st/${sid}/up/${uploader_id}/ta/${take_id}.${ext}` : `${storageDir}/us/${uploader_id}/tmp/${take_id}.${ext}`;
    if (image_path) {
      const uploadPromise = uploadBlob({ blob: file, dispatch: dispatch, path: image_path, firebase: firebase })
      let result = await uploadPromise;
      if (result === 'upload completed' && shot_id && shot_id != "reference") {
        toast.success(Messages.info.imageUploadCompleted);
        if (callback) {
          callback({ take_id: take_id, image_path: image_path, file: file });
        }
        const im_payload = {
          image_path: image_path,
          thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
          shot_id: shot_id,
          take_id: take_id,
          is_ref: is_ref,
          is_vertical: is_vertical,
          uploader_email: currentUser?.email || null
        }
        dispatch({ type: "ADD_IMAGE_TO_SHOT", payload: im_payload });
        //setSelected();
        //setTakesCount(takesCount + 1)
        const lastPayload = {
          take_id: take_id,
          image: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
          stream_path: image_path, //path, 
          shot_id: shot_id
        };
        dispatch({ type: "UPDATE_SHOT_LAST_TAKE", payload: lastPayload })
      } else if (result === 'upload completed') {
        if (is_ref) {
          if (storyRef) {
            const take_payload = {
              image_path: image_path,
              thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_thumb_vert'] : assets['processing_thumb'],
              shot_id: "reference",
              id: take_id,
              is_ref: true,
              is_vertical: is_vertical
            }
            if (callback) callback({ take: take_payload, image_path: image_path, file: file });
            dispatch({ type: "ADD_TAKE_REF", payload: { take: take_payload, addUserRef: addUserRef } });
          } else {
            if (callback) callback({ image_path: image_path, file: file });
            dispatch({ type: "ADD_TMP_REF", payload: { take_id: take_id, path: image_path, file: file } })
          }
        }
      } else if (callback) {
        callback({ image_path: image_path, file: file });
      }
    }
  }
}

export const debounce = (func, wait = 300) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  };
}

export const audioUpload = async (props) => {
  const { file, state } = props;
  if (!file) throw Error("No file provided for upload.");
  const currentUserId = state.userData.ref.id
  const file_MB = file.size / 1000000;
  const max_MB = 10; // Set a reasonable limit for audio files

  if (file_MB > max_MB) {
    alert(`Please use a smaller file size. ${file_MB.toPrecision(3)}MB is over the ${max_MB}MB limit.`);
    // throw new Error('File size exceeds limit.');
    throw Error("File size exceeds limit.");
  } else {
    const audio_id = uuidv4();
    const storageDir = "media";
    let oid = "unknown";
    let sid = "unknown";

    if (state?.stories?.activeStory && state?.stories?.activeStory?.ref) {
      const story_path_array = state.stories.activeStory.ref.path.split("/");
      const OID_ind = story_path_array.findIndex((item) => item === "users") + 1;
      oid = story_path_array[OID_ind];

      const SID_ind = story_path_array.findIndex((item) => item === "stories") + 1;
      sid = story_path_array[SID_ind];
    }

    const uploader_id = currentUserId ? currentUserId : "unknown";
    const audio_path = `${storageDir}/us/${oid}/st/${sid}/up/${uploader_id}/audio/${audio_id}.mp3`;

    const storageRef = ref(storage, audio_path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          // Handle progress updates if needed
        },
        (error) => {
          console.error('Error uploading audio:', error);
          // toast.error('Audio upload failed.');
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(storageRef);
            // toast.success('Audio upload completed.');
            resolve({ audio_path, downloadURL });
          } catch (error) {
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  }
};

export const createVeneer = async (props) => {
  const { prompt, strength, state, dispatch, method_name, is_vertical, method, engine, video_path, shot_id, take_id, ancestor_take_id, cost, composition, style, ref_images } = props;
  const storyRef = state.stories.activeStoryRef;
  const story = state.stories.activeStory;
  const seed = -1;
  const random_seed = seed == -1 ? Math.floor(Math.random() * 100000) + 101000000 : 10;
  const veneer_id = nanoid();  // the new take_id is matched for the veneer and the corresponding take
  const ancestor_id = take_id; // this is a hack, not the true anscestor.  Its jusyt the parent for debug. ToDo: Need to pass this in.
  const creator_id = state.userData?.ref?.id || "unknown";
  const creator_email = state.userData?.email || "unknown";

  // Compute credit split between project and user
  const { projectAmount, userAmount, payer } = await computeCreditSplit(storyRef, cost, creator_id);

  const veneer = {
    video_path: video_path,
    audio: "none",
    prompt: prompt,
    strength: strength,
    method_name: method_name,
    method: method,
    engine: engine,
    seed: random_seed,
    shot_id: shot_id,
    take_id: veneer_id,                                      // this is the ID that matches the take that holds veneer, and its used by the backend align to update the take below
    parent_take_id: take_id || null,                                 // for records, we keep a copy of the source take id
    ancestor_take_id: (ancestor_take_id) ? ancestor_take_id : take_id || null,  // oldest known ancestral take_id - the root of a tree
    created_at: serverTimestamp(),
    creator_id: creator_id,
    creator_email: creator_email,
    completed_at: null,
    ref_images: ref_images || [],
    composition: composition,
    style: style,
    cost: cost,
    is_vertical: is_vertical,
    payer: payer,
  };
  await setDoc(doc(storyRef, "veneer", veneer_id), veneer).then(async () => {
    // Deduct from project credits if applicable
    if (projectAmount > 0) {
      const projectBalanceRef = doc(storyRef, 'credits', '0');
      await setDoc(projectBalanceRef, { credits: increment(-projectAmount), updated_at: serverTimestamp() }, { merge: true });
    }
    // Deduct from user credits
    if (userAmount > 0) {
      dispatch({ type: "UPDATE_USER_CREDITS", payload: { credits: -userAmount } });
    }
    if (story.isCompetition && (story.timerStart == undefined || story.activeStory.timerStart == null)) {
      const timerStart = Date.now();
      const payload = { timerStart: timerStart };
      dispatch({ type: "START_STORY_TIMER", payload: payload });
    }
    const takePayload = {
      video_path: assets['processing_veneer_thumb_path'],
      thumbnail: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_gif'] : assets['processing_veneer_thumb_gif'], // assets['processing_veneer_thumb'],
      shot_id: shot_id,
      take_id: veneer_id,
      source: "generation",
      generate: true
    }
    dispatch({ type: "ADD_TAKE_TO_SHOT", payload: takePayload })
    const shotPayload = {
      take_id: veneer_id,
      // uploader_id: "veneer", // msg.input.user_id,   // we did this when it was in the cloud  ... now its a name, so autoveneer [disabled] might apply to veneer as well?
      // media_source: "veneer", // ToDo:  this for upload & capture too, also yt
      image: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_gif'] : assets['processing_veneer_thumb_gif'],
      stream_path: state.stories.activeStory?.is_vertical ? assets['processing_veneer_vert_streampath'] : assets['processing_veneer_streampath'],
      shot_id: shot_id,
    };

    dispatch({ type: "UPDATE_SHOT_LAST_TAKE", payload: shotPayload })
  })
    .catch((error) => {
      console.log("Error creating veneer request:", veneer);
    });
  console.log("tried VENEER:", veneer);
}

export const createVeneerNewTake = debounce((props) => {
  const { prompt, reimagine, reimagineVeneer, state, dispatch, is_vertical, chosenMethod, chosenModel, shotId, strength, selectedComp, selectedStyle, customStyle, customComp, cost, imageRefs } = props;
  const modelsToExclude = ['vid2vid', 'comfy-FFAutoCine-v0', 'haiper-image'];
  const selectedReimagineItem = reimagine;
  const pathSegments = location.pathname.split('/');
  const shot_id = shotId || pathSegments[pathSegments.length - 1];
  const takesList = sortTakes(state.stories.activeTakeList.filter(take => take.shot_id === shot_id))
  const models = hostConfig.aiModels;
  const cost_per_veneer = cost || hostConfig.veneerCost;
  const ref_images = imageRefs ? imageRefs : [];
  if (prompt === '' && !modelsToExclude.some(model => chosenModel.includes(model))) {
    alert("Please add prompt to create");
    return
  }
  let method_name = "im2im-v4";
  let method = "im2im";
  let engine = "stabilityai/stable-diffusion-2-depth";
  let video_path = null;
  let video_id = null;
  if (selectedReimagineItem) {
    video_path = selectedReimagineItem?.video_path; // the full path w/o the storage bucket
    video_id = selectedReimagineItem?.id;         // for records, we keep a copy of the source video_id as the parent_take_id
  }
  const take_id = (selectedReimagineItem) ? selectedReimagineItem?.id : (ref_images.length > 0) ? state.stories.activeTakeList.find(ref => ref.image_path === ref_images[0])?.id : null;
  const ancestor_take_id = (reimagineVeneer?.ancestor_take_id) ? reimagineVeneer.ancestor_take_id : null

  /*if (chosenModel !== null) {
    method_name = chosenModel.split(',')[0];
    method = chosenModel.split(',')[1];
    engine = chosenModel.split(',')[2];
  }*/
  models.forEach((value, key) => {
    if (key == chosenModel) {
      method_name = key;
      method = value.method;
      engine = value.engine;
      return;
    }
  });
  if (method == "text2vid") {
    // anything that starts w text2video has an API shortcut, where is needs to pass upa video, but does not use it
    // TO DO: Remove defaultPath and replace with null ... which should work if we depricate zeroscope
    video_path = hostConfig.env == "prod" ? "https://storage.googleapis.com/v0zebra.appspot.com/" + baseVideos[hostConfig.env] : "https://storage.googleapis.com/v0zebra-dev.appspot.com/" + baseVideos[hostConfig.env];
    video_id = "";
  }
  createVeneer({ state: state, dispatch: dispatch, is_vertical: is_vertical, prompt: prompt, strength: strength, method_name: method_name, method: method, engine: engine, video_path: video_path, shot_id: shot_id, video_id: video_id, take_id: take_id, ancestor_take_id: ancestor_take_id, cost: cost_per_veneer, composition: { selected: selectedComp, custom: customComp }, style: { selected: selectedStyle, custom: customStyle }, ref_images: ref_images }); // cost is fixed at 10.  fps not under control. selectedItem.dur * fps * lookup
  dispatch({ type: "UPDATE_STORY_SHOT_PROMPT", payload: { prompt: prompt, /*strength: strength, */shot_id: shot_id, /*model: method_name, */composition: { selected: selectedComp, custom: customComp }, style: { selected: selectedStyle, custom: customStyle } } });

  toast.success(Messages.info.videoGenerationStarted);
}, 300)
/* Added by Philip 
const createVeneerForTake = () => {
    const modelsToExclude = ['vid2vid', 'comfy-FFAutoCine-v0', 'haiper-image'];
    if (prompt === '' && !modelsToExclude.some(model => chosenModel.includes(model))) {
      alert("Please add prompt to create");
      return
    }
    console.log("Creating veneer for take", selectedItem);

    if(story.isCompetition && story.timerStart != null && (Date.now() - story.timerStart)/1000 > 1800) {
      setShowReimagine(null)
      setCompAlertOpen(true)
      return
    }
    let method_name = "im2im-v4";
    let method = "im2im";
    let engine = "stabilityai/stable-diffusion-2-depth";
    let video_path = null;
    let video_id = null;
    if(selectedReimagineItem) {
      video_path = selectedReimagineItem?.video_path; // the full path w/o the storage bucket
      video_id = selectedReimagineItem?.id;         // for records, we keep a copy of the source video_id as the parent_take_id
    } 
    // compute ancestor_take_id - oldest known ancestral take_id - the root of a tree to the first video
    const this_take =  takesList.filter((take) => take.id == video_id)[0]  
    let ancestor_take_id = ((this_take) && 'ancestor_take_id' in this_take  && (this_take.ancestor_take_id)) ? this_take.ancestor_take_id : video_id 

    models.forEach((value, key) => {
      if (key == chosenModel) {
        method_name = key;
        method = value.method;
        engine = value.engine;
        return;
      }
    });
    if (method == "text2vid") {
      // anything that starts w text2video has an API shortcut, where is needs to pass upa video, but does not use it
      // TO DO: Remove defaultPath and replace with null ... which should work if we depricate zeroscope
      let defaultPath =
        hostConfig.env == "prod"
          ? "https://storage.googleapis.com/v0zebra.appspot.com/p/st/PsTKsKgJLNGsMiSDiAao/sh/qTF8fXtuzjPuQjj4hJ0aO/ta/cfc95bce-c1a4-43ec-9362-96944a9ebab8/q/2023-07-19T18_27_15_467Z-1.mp4"
          : "https://storage.googleapis.com/v0zebra-dev.appspot.com/p/st/kJW-WfbW-yS9pTY0sxJd/sh/94a49eff-460a-4ebf-af29-9aa7ffd04086/ta/7ab97cde-0230-4730-b5e6-17e0436cf4e7/q/2023-09-28T06-55-41-041Z-1.mp4";
      video_path = defaultPath;
      video_id = "";
    }
    console.log("Creating veneer for take - text2vid", selectedItem);
    createVeneer(prompt, strength, method_name, method, engine, video_path, shot_id, video_id, ancestor_take_id, costPerVeneer); // cost is fixed at 10.  fps not under control. selectedItem.dur * fps * lookup
    dispatch({ type: "UPDATE_STORY_SHOT_PROMPT", payload: { prompt: prompt, strength: strength, shot_id: shot_id, model: chosenModel } });

    toast.success(Messages.info.videoGenerationStarted);

    setSelectedReimagineItem(null);
    setShowReimagine(null);
  }
  */







/*export const createVeneerNewShot = (props) => {
  const {state, dispatch, currentUser, prompt, reimagine, chosenMethod, chosenModel, strength, selectedComp, selectedStyle, customStyle, customComp} = props;
  const shotId = nanoid();
  const models = hostConfig.aiModels;
  const cost_per_veneer = hostConfig.veneerCost;
  //const modelsToExclude = ['vid2vid', 'comfy-FFAutoCine-v0', 'haiper-image'];
  if (prompt === ''/* && !modelsToExclude.some(model => chosenModel.includes(model))*) {
    alert("Please add prompt to create");
    return
  }
  //if(props.textPrompt) setChosenModel("haiper-text");
  console.log("prompt", prompt) //, selectedShot, selectedItem))
  console.log("strength", strength) //, selectedShot, selectedItem))
  console.log("model", chosenModel) //, selectedShot, selectedItem))
  let method = chosenMethod;
  let method_name = chosenModel;
  //console.log("ShotId", shot_id) //, selectedShot, selectedItem))
  //console.log("selectedItem", selectedReimagineItem) //, selectedShot, selectedItem))
  /*let method_name = "im2im-v4";
  let method = "im2im";
  let engine = "stabilityai/stable-diffusion-2-depth";
  let video_path = selectedReimagineItem?.video_path; // the full path w/o the storage bucket
  let video_id = selectedReimagineItem?.id;         // for records, we keep a copy of the source video_id as the parent_take_id
*
  // compute ancestor_take_id - oldest known ancestral take_id - the root of a tree to the first video
  //const this_take =  takesList.filter((take) => take.id == video_id)[0]  
  //let ancestor_take_id = ((this_take) && 'ancestor_take_id' in this_take  && (this_take.ancestor_take_id)) ? this_take.ancestor_take_id : video_id 

  /*if (chosenModel !== null) {
    method_name = chosenModel.split(',')[0];
    method = chosenModel.split(',')[1];
    engine = chosenModel.split(',')[2];
  }*

  addEmptyShot(shotId)

  models.forEach((value, key) => {
    if (key == chosenModel) {
      method_name = value.method_name;
      method = value.method;
      engine = value.engine;
    }
  });

  if (models.has(chosenModel) && models.get(chosenModel).method == "text2vid") {
    // anything that starts w text2video has an API shortcut, where is needs to pass upa video, but does not use it
    // TO DO: Remove defaultPath and replace with null ... which should work if we depricate zeroscope
    let defaultPath =
      hostConfig.env == "prod"
        ? "https://storage.googleapis.com/v0zebra.appspot.com/p/st/PsTKsKgJLNGsMiSDiAao/sh/qTF8fXtuzjPuQjj4hJ0aO/ta/cfc95bce-c1a4-43ec-9362-96944a9ebab8/q/2023-07-19T18_27_15_467Z-1.mp4"
        : "https://storage.googleapis.com/v0zebra-dev.appspot.com/p/st/kJW-WfbW-yS9pTY0sxJd/sh/94a49eff-460a-4ebf-af29-9aa7ffd04086/ta/7ab97cde-0230-4730-b5e6-17e0436cf4e7/q/2023-09-28T06-55-41-041Z-1.mp4";
    video_path = defaultPath;
    video_id = "";
  }
  createVeneer({ state:state, dispatch:dispatch, prompt: prompt, strength: strength, method_name: method_name, method: method, engine: engine, video_path: video_path, /*shot_id: shot_id, video_id: video_id, ancestor_take_id: ancestor_take_id, *cost_per_veneer: cost_per_veneer, composition: { selected: selectedComp, custom: customComp }, style: { selected: selectedStyle, custom: customStyle } }); // cost is fixed at 10.  fps not under control. selectedItem.dur * fps * lookup
  //dispatch({ type: "UPDATE_STORY_SHOT_PROMPT", payload: { prompt: prompt, strength: strength, shot_id: shot_id, model: chosenModel } });

  if (method == "im2im") {
    alert("Creation started. Your new video will be added in 15-30 mins.");
  } else {
    toast.success(Messages.info.videoCreationStarted);
  }
*/

export const addEmptyShot = async (props) => {
  const { dispatch, state, uploading, shotId, partId, insertAtIndex, prevShot, nextShot } = props
  //let description = props.next_shot_index ? props.next_shot_index + ":" : null;
  let newShotId = shotId ? shotId : nanoid()
  let part_id = partId || state.stories.activeStory.parts[0].id;
  const random_int = Math.floor((Math.random() * 18))
  const biased_int = [0, 0, 1, 1, 1, 1, 2, 2, 3, 3, 5, 5, 6, 6, 7, 7, 8, 8, 0][random_int]
  const shot_type_filenames = ["wide.png", "mid.png", "action.png", "close.png", "empty.png", "xcu.png", "two_shot.png", "ots.png", "full.png", "action_close.png",]
  const shot_type = shot_type_filenames[biased_int]
  const random_image = assets["empty_thumbnail"];
  // const random_image = `https://storage.googleapis.com/v0zebra.appspot.com/resource/shot_type/${shot_type}`
  const new_panel_desc = ['Wide shot', 'Mid shot', 'Action shot', 'Close up of face', '', 'Extreme close up', 'Two-Shot', 'Over the shoulder', 'Full shot', 'Action Close up'][biased_int]
  //if(!description) description = new_panel_desc
  //const one_word_title = description.substring(description.lastIndexOf(" ") + 1)
  //const punctuation_free_title = one_word_title.replace(/[".,\/#!$%\^\&\*;:{}=\-_`~()]/g, "")
  //const capitalized_title = punctuation_free_title[0].toUpperCase() + punctuation_free_title.substring(1);

  let title;
  const isInsertingInbetween = typeof insertAtIndex === 'number';
  if (isInsertingInbetween) {
    let prev = prevShot;
    let next = nextShot;
    if (!prev || !next) {
      const part = state.stories?.activeStory?.parts?.find(p => p.id === part_id);
      if (part && Array.isArray(part.order)) {
        if (!prev && insertAtIndex > 0) {
          prev = state.stories?.activeShotList?.find(s => s.id === part.order[insertAtIndex - 1]);
        }
        if (!next && insertAtIndex < part.order.length) {
          next = state.stories?.activeShotList?.find(s => s.id === part.order[insertAtIndex]);
        }
      }
    }
    title = incrementShotTitle(prev?.header, next?.header);
  } else {
    title = "Shot " + (state.stories.activeShotList.length + 1);
  }
  const newShot = {
    id: newShotId,
    header: title,
    image: random_image,
    meta: "",
    prompt: "",
    description: "",
    last_take_id: null,
    selected_take_id: null,
    last_take_im: (uploading) ? state.stories.activeStory?.is_vertical ? assets["processing_thumb_vert"] : assets["processing_thumb"] : null,
    last_take_stream_url: null,
    deleted_at: null
  };
  const payload = {
    part_id: part_id,
    shot: newShot,
    shot_id: newShot.id,
    auto_title: !isInsertingInbetween,
    ...(isInsertingInbetween ? { index: insertAtIndex } : {}),
  }
  dispatch({ type: "ADD_STORY_SHOT", payload: payload });
}

export const btnObj2Arr = (props) => {
  const buttons = props.buttons;
  let btnArr = [];
  if (buttons && buttons.length) {
    buttons.map((button, key) => {
      if (button != null && !button.disabled) {
        if (button.type == "icon") {
          btnArr.push(<Link key={key} title={button.text} onClick={() => {
            if (button.href) navigate(button.href)
            else if (button.click) button.click()
          }}>{button.icon && <button.icon className="icon" />}</Link>)
        } else {
          let icon = <></>
          if (button.icon) icon = null //icon = iconMap[button.icon]
          if (button.type == "secondary") {
            btnArr.push(<Button key={key} variant="contained" disabled={button.disabled} color="secondary" onClick={() => {
              if (button.href) navigate(button.href)
              else if (button.click) button.click()
            }}>{icon}{button.text}</Button>)
          } else if (button.type == "tertiary") {
            btnArr.push(<Button key={key} variant="outlined" disabled={button.disabled} onClick={() => {
              if (button.href) navigate(button.href)
              else if (button.click) button.click()
            }}>{icon}{button.text}</Button>)
          } else {
            btnArr.push(<Button key={key} variant="contained" disabled={button.disabled} onClick={() => {
              if (button.href) navigate(button.href)
              else if (button.click) button.click()
            }}>{icon}{button.text}</Button>)
          }
        }
      }
    });
  }
  return btnArr;
}

export const handleContinueEmpty = ({ dispatch, portalId, includeShot, includePart = true }) => {
  const id = portalId || nanoid(12);
  const shot = includeShot !== undefined ? includeShot : false;
  const story = emptyStory(id, includeShot, undefined, includePart);
  dispatch({ type: "NEW_ACTIVE_STORY", payload: { newStory: story } });
};

export const addShotUserRef = ({ dispatch, state, shot, forceRefType = false, currentUser, activeStoryRef }) => {
  console.log("ADDING SHOT REF", state.generate)
  const getLastTake = () => {
    const filteredTakes = state.stories.activeTakeList.filter(take => take.shot_id === shot.id)
    return sortTakes(filteredTakes)[0]
  };

  const take = (state.stories.activeTakeList.find(take => take.id === shot.selected_take_id))
    ? state.stories.activeTakeList.find(take => take.id === shot.selected_take_id)
    : getLastTake();

  addTakeUserRef({ dispatch, state, takes: [take], forceRefType, currentUser, activeStoryRef });
};

export const addImgUserRefs = ({ dispatch, state, refs, currentUser, activeStoryRef }) => {
  let takes = []
  refs.forEach(ref => {
    const path = ref.path;
    let take = state.stories.activeTakeList.find(take => take.thumbnail === path || take.image_path === path || take.stream_path === path) || null;

    if (!take) {
      take = state.stories.activeRefList?.find(take => take.thumbnail === path || take.image_path === path || take.stream_path === path) || null;
    }

    if (!take) {
      take = {
        id: uuidv4(),
        thumbnail: path,
        image_path: path,
        stream_path: path,
        is_ref: true,
        shot_id: "reference"
      };
      dispatch({ type: "ADD_TAKE_REF", payload: { take: take } });
    }

    if (take) takes.push(take);
  });
  addTakeUserRef({ dispatch, state, takes, currentUser, activeStoryRef });
};

export const getAvailableModels = (method, disallowedModels = []) => {
  const models = hostConfig.aiModels;
  const modelsArr = Array.from(models, ([key, data]) => ({ key, data }))
  if (method) return modelsArr.filter(item => item.data.method === method && !disallowedModels.includes(item.key))
  return modelsArr.filter(item => !disallowedModels.includes(item.key))
};

export const addTakeUserRef = ({ dispatch, state, takes, forceRefType = false, currentUser, activeStoryRef }) => {
  const userRefs = state.generate?.userRefs || [];
  const active = userRefs?.filter(ref => ref.active).length || 0;
  const defaultModels = state.stories.activeStory?.defaultModels || [];
  let updateModel = false;
  let updateMethod = false;
  const getNewRefType = (take) => {
    let ref;
    switch (state.generate.method) {
      case "vid2vid":
        return userRefs?.find(ref => ref.active && ref.type === "vid") ? "ref" : "vid";
      case "im2vid":
        return userRefs?.find(ref => ref.active && ref.type === "first") ? "ref" : "first";
      case "text2vid":
        ref = (userRefs?.find(ref => ref.active && ref.type === "first") || !userRefs.length) ? "first" : "ref";
        if (take && !state.stories.activeVeneerLUT?.[take.id] && !userRefs?.find(ref => ref.active && ref.type === "vid")) {
          ref = "vid";
        }
        if (ref === "vid") {
          updateMethod = "vid2vid";
        } else if (ref === "first") {
          updateMethod = "im2vid";
        }
        return ref;
      default:
        return "ref";
    }
  }
  if (state.generate.method === "text2im") updateMethod = "im2im";
  // Resolve the method to use for model/ref lookups. The reducer guarantees "text2im" as
  // the default, but old user_state docs may have stored null — guard here as well.
  const effectiveMethod = updateMethod || state.generate.method || "text2im";
  const availableModels = getAvailableModels(effectiveMethod, state.generate.disallowedModels);
  const currModel = state.generate.model && availableModels.find(model => model.key === state.generate.model) ? state.generate.model : null;
  const models = hostConfig.aiModels;
  const defaultModel = (defaultModels && defaultModels[effectiveMethod]) ? defaultModels[effectiveMethod] : methods.get(effectiveMethod)?.defaultModel;
  const model = currModel || defaultModel;
  const method = methods.get(effectiveMethod);
  const modelData = models.get(model);
  if (!modelData) {
    console.warn('[addTakeUserRef] bailing — no modelData for model:', model, 'effectiveMethod:', effectiveMethod);
    return;
  }
  const max = modelData.maxRefs || 0;
  const total = max + (method.requiresMedia ? 1 : 0);
  let newUserList = [];
  let toasted = false;
  let currentActive = active;

  for (const take of takes) {
    if (userRefs.length && userRefs.find(obj => obj.id === take.id)) {
      toast.error("This reference take has already been added.");
      toasted = true;
      continue;
    }
    console.log("userRefs", userRefs.length, total);

    const newRefType = forceRefType ? "ref" : getNewRefType(take);
    if (!take.is_ref && !state.stories?.activeRefList.find(ref => ref.id === take.id)) {
      dispatch({ type: "UPDATE_TAKE", payload: { ...take, is_ref: true } });
    }
    const isActive = currentActive < total;
    if (isActive) currentActive++;
    newUserList.push({ id: take.id, active: isActive, type: newRefType });
  }
  if (newUserList.length === 0) return;
  newUserList = [...userRefs, ...newUserList];
  // Persist refs to Firebase.
  persistUserState({ patch: { userRefs: newUserList }, dispatch, currentUser, activeStoryRef, state });
  if (!toasted) toast.success('Adding reference...');
}

export const handleModelLimit = (props) => {
  const { total, model, newMethod, state, silent = false } = props;
  let newModel = false;
  const models = hostConfig.aiModels;
  const rawMethod = newMethod || state.generate.method;
  const method = rawMethod === 'text2im' ? 'im2im' : rawMethod;
  const disallowedModels = state.generate.disallowedModels || [];
  const availableModels = getAvailableModels(method, disallowedModels);
  const modelData = models.get(model);
  if (!modelData) return false;

  const methodData = methods.get(method);
  const requiresMedia = methodData?.requiresMedia ? 1 : 0;
  const filtered = availableModels.filter(item =>
    item.data.method === method &&
    (item.data.maxRefs || 0) + requiresMedia >= total
  );
  if (filtered.length == 0) {
    if (!silent) {
      if (state.generate.method == "vid2vid" || state.generate.method == "im2vid") toast.error(`Maximum number of refs reached. If you want to add more refs, change the ref types to "reference"`);
      else toast.error(`Maximum number of refs reached for this output method`);
    }
    return false
  }
  const family = filtered.find(item => item.data.family == modelData.family)
  if (family) {
    newModel = family.key
  } else {
    newModel = filtered[0].key;
  }
  if (newModel && models.get(newModel)) {
    if (!silent) {
      toast.success("Switching to model " + models.get(newModel)?.name + " to accommodate additional references.");
    }
    return newModel;
  } else {
    return false;
  }
}

export const getRefImages = async (veneer) => {
  console.log("ref images", veneer);
  if (!veneer) return [];
  let refs = [];
  if (veneer.ref_images && veneer.ref_images.length > 0) {
    for (let ref_image of veneer.ref_images) {
      if (!ref_image) continue;
      const url = await getMediaUrl(ref_image);
      if (url) refs.push({ id: uuidv4(), path: ref_image, url: url, type: "ref" });
    }
  }
  if (veneer.video_path && veneer.video_path.indexOf(baseVideos["prod"]) === -1 && veneer.video_path.indexOf(baseVideos["dev"]) === -1) {
    const url = await getMediaUrl(veneer.video_path);
    if (url) refs.push({ id: uuidv4(), path: veneer.video_path, url: url, type: "vid" });
  }
  return refs;
}

/**
 * persistUserState — the ONLY function that writes to user_state/{uid} subcollection.
 *
 * Atomically writes generate UI state to Firebase and dispatches the
 * corresponding AppContext actions in a single call.
 *
 * Firebase schema (unchanged): { method, model, prompt, userRefs }
 *   - "model" in Firebase = user's explicitly chosen preferred model only.
 *   - System-driven model overrides must use dispatch({ type: "UPDATE_SYSTEM_MODEL" }) directly.
 *
 * @param {Object}            patch           - Keys: userRefs, method, model, prompt
 * @param {Function}          dispatch        - AppContext dispatch
 * @param {Object}            [currentUser]   - Firebase auth user (falls back to auth.currentUser)
 * @param {DocumentReference} [activeStoryRef] - Firestore story ref (falls back to state.stories.activeStoryRef)
 * @param {Object}            [state]         - AppContext state (used as fallback for activeStoryRef)
 */
export const persistUserState = async ({ patch, dispatch, currentUser, activeStoryRef, state }) => {
  // Resolve currentUser: caller-provided > auth singleton fallback
  const user = currentUser || auth.currentUser;
  // Resolve activeStoryRef: caller-provided > state.stories.activeStoryRef fallback
  const storyRef = activeStoryRef || state?.stories?.activeStoryRef;

  if (!user || !storyRef) {
    console.warn('[persistUserState] Missing user or activeStoryRef — dispatching in-memory only', { user: !!user, storyRef: !!storyRef });
    // Still dispatch in-memory updates even if we can't write to Firebase
    if ('userRefs' in patch) dispatch({ type: 'UPDATE_USER_REFS', payload: patch.userRefs });
    if ('method' in patch) dispatch({ type: 'UPDATE_SELECTED_METHOD', payload: patch.method });
    if ('model' in patch) {
      dispatch({ type: 'UPDATE_SELECTED_MODEL', payload: patch.model });
      dispatch({ type: 'UPDATE_SYSTEM_MODEL', payload: null });
    }
    if ('prompt' in patch) dispatch({ type: 'UPDATE_PROMPT', payload: patch.prompt });
    return;
  }

  // Only these four keys are ever written to Firebase — schema stays backwards-compatible
  const firestorePatch = {};
  if ('userRefs' in patch) firestorePatch.userRefs = patch.userRefs;
  if ('method' in patch) firestorePatch.method = patch.method;
  if ('model' in patch) firestorePatch.model = patch.model;  // user preference only
  if ('prompt' in patch) firestorePatch.prompt = patch.prompt;

  if (Object.keys(firestorePatch).length > 0) {
    const userStateRef = doc(storyRef, 'user_state', user.uid);
    try {
      await setDoc(userStateRef, firestorePatch, { merge: true });
    } catch (err) {
      console.error('[persistUserState] setDoc FAILED', { path: userStateRef.path, code: err.code, message: err.message });
    }
  }

  // Dispatch in-memory updates
  if ('userRefs' in patch) dispatch({ type: 'UPDATE_USER_REFS', payload: patch.userRefs });
  if ('method' in patch) dispatch({ type: 'UPDATE_SELECTED_METHOD', payload: patch.method });
  if ('model' in patch) {
    // Writing user's preferred model also resets systemModel so UI immediately reflects the choice
    dispatch({ type: 'UPDATE_SELECTED_MODEL', payload: patch.model });
    dispatch({ type: 'UPDATE_SYSTEM_MODEL', payload: null });
  }
  if ('prompt' in patch) dispatch({ type: 'UPDATE_PROMPT', payload: patch.prompt });
};

// ---------------------------------------------------------------------------
// Member helpers
// ---------------------------------------------------------------------------

/**
 * Look up a platform user by email address.
 * Returns { uid, data } if found, or null if no account exists.
 *
 * @param {string} email
 * @returns {Promise<{ uid: string, data: object }|null>}
 */
export const lookupUserByEmail = async (email) => {
  const { collection: col, query: q, where: wh, getDocs: gd } = await import("@firebase/firestore");
  const snapshot = await gd(q(col(db, "users"), wh("email", "==", email.trim().toLowerCase())));
  if (snapshot.empty) return null;
  const userDoc = snapshot.docs[0];
  return { uid: userDoc.id, data: userDoc.data() };
};

/**
 * Fetch Firestore profile documents for a list of member UIDs.
 * Returns a map of { uid: profileData } for UIDs not already in `existing`.
 *
 * @param {Array<{ uid: string|null }>} members
 * @param {object} existing  - Already-cached profiles map to skip
 * @returns {Promise<object>}
 */
export const fetchMemberProfiles = async (members, existing = {}) => {
  const { doc: d, getDoc: gd } = await import("@firebase/firestore");
  const profiles = {};
  for (const member of members) {
    if (member.uid && !existing[member.uid]) {
      try {
        const snap = await gd(d(db, "users", member.uid));
        if (snap.exists()) profiles[member.uid] = snap.data();
      } catch (err) {
        console.error("fetchMemberProfiles: error fetching uid", member.uid, err);
      }
    }
  }
  return profiles;
};

/**
 * Send a project invite email via the sendProjectInvite Cloud Function.
 *
 * @param {{ email: string, inviteCode: string, portalId: string, projectName: string, inviterName: string }} opts
 * @returns {Promise<void>}
 */
export const sendMemberInviteEmail = async ({ email, inviteCode, portalId, projectName, inviterName }) => {
  const { httpsCallable } = await import("firebase/functions");
  const { functionsInstance } = await import("../utils/firebase");
  const fn = httpsCallable(functionsInstance, "sendProjectInvite");
  await fn({ email, inviteCode, portalId, projectName, inviterName });
};

/**
 * Send a project-added notification email to an existing platform user.
 * Unlike sendMemberInviteEmail, no invite code is needed — the user already
 * has access. This is a courtesy notification with a direct link to the project.
 *
 * Errors are intentionally not re-thrown; callers should treat this as
 * fire-and-forget so a failed notification never blocks the add-member flow.
 *
 * @param {{ email: string, portalId: string, projectName: string, inviterName: string }} opts
 * @returns {Promise<void>}
 */
export const sendMemberAddedEmail = async ({ email, portalId, projectName, inviterName }) => {
  const { httpsCallable } = await import("firebase/functions");
  const { functionsInstance } = await import("../utils/firebase");
  const fn = httpsCallable(functionsInstance, "sendProjectAdded");
  await fn({ email, portalId, projectName, inviterName });
};

/**
 * Send a credit request email to the project owner.
 * Calls the `sendCreditRequest` Cloud Function which looks up the owner's
 * email server-side and delivers a branded notification.
 *
 * @param {{ creatorUid: string, portalId: string, projectName: string, requesterEmail: string, requesterName: string, lastPrompt?: string, lastVeneerUrl?: string }} opts
 * @returns {Promise<void>}
 */
export const sendCreditRequestEmail = async ({ creatorUid, portalId, projectName, requesterEmail, requesterName, lastPrompt, lastVeneerUrl }) => {
  const { httpsCallable } = await import("firebase/functions");
  const { functionsInstance } = await import("../utils/firebase");
  const fn = httpsCallable(functionsInstance, "sendCreditRequest");
  await fn({ creatorUid, portalId, projectName, requesterEmail, requesterName, lastPrompt, lastVeneerUrl });
};

// ---------------------------------------------------------------------------
// Project credits helpers
// ---------------------------------------------------------------------------

/**
 * Load a project's credits balance and full funding history from Firestore.
 * Self-heals by initializing the credits doc if it doesn't exist yet.
 *
 * @param {import("@firebase/firestore").DocumentReference} storyRef
 * @returns {Promise<{ credits: number, history: Array }>}
 */
export const loadProjectCreditsData = async (storyRef) => {
  const { doc: d, getDoc: gd, setDoc: sd, collection: col, getDocs: gds, query: q, orderBy: ob } = await import("@firebase/firestore");
  const creditsRef = d(storyRef, "credits", "0");
  const creditsSnap = await gd(creditsRef);

  let credits = 0;
  if (!creditsSnap.exists()) {
    await sd(creditsRef, { credits: 0, policy: "projectThenUser" });
  } else {
    credits = creditsSnap.data().credits || 0;
  }

  const fundedSnap = await gds(q(col(creditsRef, "funded"), ob("date", "desc")));
  const history = fundedSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  return { credits, history };
};

/**
 * Refresh a project's credits balance and funding history (used after transfers).
 * Same as loadProjectCreditsData but without the self-heal write.
 *
 * @param {import("@firebase/firestore").DocumentReference} storyRef
 * @returns {Promise<{ credits: number, history: Array }>}
 */
export const refreshProjectCreditsData = async (storyRef) => {
  const { doc: d, getDoc: gd, collection: col, getDocs: gds, query: q, orderBy: ob } = await import("@firebase/firestore");
  const creditsRef = d(storyRef, "credits", "0");

  const [creditsSnap, fundedSnap] = await Promise.all([
    gd(creditsRef),
    gds(q(col(creditsRef, "funded"), ob("date", "desc"))),
  ]);

  const credits = creditsSnap.exists() ? creditsSnap.data().credits || 0 : 0;
  const history = fundedSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  return { credits, history };
};
